import { go } from "fuzzysort";
import { type FC, type Key, useCallback, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Item } from "react-stately";
import { ComboBox, MenuOptionItem } from "widgets";

import { getIconElementForPlaybook, isPlaybookExecutableInPlaybookPageContext } from "@/app-utils/playbook.ts";
import { type Playbook } from "@/graphql.ts";
import { openPlaybook } from "@/redux/modules/playbook-execution.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { ProtectedResourceLogo } from "@/ui-modules/core/ProtectedResourceLogo.tsx";

const playbookFilterOtions = {
  keys: ["name", "description"],
  limit: 15, // don't return more results than you need!
  threshold: -1000, // don't return bad results
};

interface PlaybookFilterProps {
  playbooks: Playbook[];
}

const PlaybookFilter: FC<PlaybookFilterProps> = (props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch<ReduxDispatch>();

  const sortedPlaybooks = useMemo(() => {
    const preparedPlaybooks: Playbook[] = props.playbooks
      .filter((playbook) => isPlaybookExecutableInPlaybookPageContext(playbook))
      .map((playbook) => {
        const title = playbook.name + (playbook.nameSuffix ? ` : ${playbook.nameSuffix}` : "");
        return { ...playbook, name: title } satisfies Playbook;
      });
    return preparedPlaybooks.sort((p1, p2) =>
      p1.name.localeCompare(p2.name, "en", {
        sensitivity: "base",
        ignorePunctuation: true,
      }),
    );
  }, [props.playbooks]);

  const [filter, setFilter] = useState<string>("");
  // if your targets don't change often, provide prepared targets instead of raw strings!
  //const filterTargets = playbooks.map(p => fuzzysort.prepare(p)) // works only with strings?!

  const filteredPlaybooks = useMemo(() => {
    if (filter) {
      const result = go(filter.trim(), sortedPlaybooks, playbookFilterOtions);
      return result.map((r) => r.obj);
    }
    return sortedPlaybooks;
  }, [filter, sortedPlaybooks]);

  // Launch the playbook from the Map, returned from Redux above.
  const onSelectionChange = useCallback(
    (playbookId: Key) => {
      const playbook = props.playbooks.find((p) => String(p.id) === String(playbookId));

      if (playbook) {
        // We need to blur the focus first, so that react-aria is not lost,
        // as a ComboBox is not designed to launch something.
        inputRef.current?.blur();
        dispatch(openPlaybook(playbook));
      }
    },
    [props.playbooks],
  );

  return (
    <ComboBox
      placeholder={"Search for Playbooks"}
      onInputChange={setFilter}
      onSelectionChange={onSelectionChange} // Will launch the Playbook
      selectedKey={null} // Always select nothing (it's controlled, as it is used as a launcher)
      autoFocus
      items={filteredPlaybooks} // The JSX bellow will be build from the items (see function)
      ref={inputRef}
      width={376}
    >
      {(playbook: Playbook) => (
        <Item key={String(playbook.id)} textValue={playbook.name}>
          <MenuOptionItem
            title={playbook.name}
            description={playbook.description}
            icon={<img width={50} src={getIconElementForPlaybook(playbook).large} alt={"Playbook icon"} />}
            tag={playbook.standard ? <ProtectedResourceLogo size={16} /> : undefined}
          />
        </Item>
      )}
    </ComboBox>
  );
};

export { PlaybookFilter };
